import React from "react";
import BookingConfirmedHeader from "./BookingConfirmedHeader";
import BookingReference from "./BookingReference";
import PaymentSuccessCard from "./PaymentSuccessCard";
import BookingDetailsCard from "./BookingDetailsCard";
import LinkedBookingsDetails, { type LinkedBookingSummary } from "./LinkedBookingsDetails";
import WhatsNextCard from "./WhatsNextCard";
import FeatureSentiment from "./FeatureSentiment";
import AlertBanner from "../shared/AlertBanner";
import type { PreOrderItem, TaxBreakdownEntry } from "../../../common/jsonRpcTypes";
import type { BookingTypeGroup } from "../../utils/bookingTypeGroups";
import PrimaryButton from "../buttons/primaryButton.tsx";
import { t } from "i18next";

type PaymentConfirmationLayoutMode =
  | "full"
  | "successOnly"
  | "successWithDetails"
  | "bookingOnly"
  | "bookingWithPreorders"
  | "enquiry";

interface PaymentConfirmationLayoutProps {
  venueName: string;
  bookingRef: string;
  depositTotal: number;
  isAuthentication: boolean;
  /**
   * Whether the pre-order was actually paid for (as opposed to pay-at-venue).
   * Only meaningful alongside `bookingJourney` + `isAuthentication`, so the
   * success message can tell "card authenticated for the deposit, pre-order
   * pay-at-venue" apart from "pre-order paid, deposit authenticated separately".
   */
  preorderPaymentTaken?: boolean;
  partySize: number;
  date: string;
  time: string;
  location: string;
  preOrderItems?: PreOrderItem[];
  /** Per-booking-type breakdown, populated only for linked bookings. */
  bookingTypeGroups?: BookingTypeGroup[];
  currency: string;
  countryCode: string | null;
  mode?: PaymentConfirmationLayoutMode;
  shouldHidePrices?: boolean;
  email?: string;
  hideHeader?: boolean;
  isPreorderCompletion?: boolean;
  hidePricesByMenu?: Record<string, boolean>;
  /**
   * Force the {@link BookingDetailsCard} to render regardless of `mode` (used on
   * the `/confirm` page so every flow shows a consistent booking summary). When
   * set, the standalone booking reference is hidden because the details card
   * already displays it.
   */
  showBookingDetails?: boolean;
  /** Render the booking-journey header title instead of the payment/order title (used on /confirm for /book). */
  bookingJourney?: boolean;
  isFromManageYourOrder?: boolean;
  /** The booking ID used when building the "Manage your order" link. */
  bookingId?: string;
  /** The HMAC signature used when building the "Manage your order" link. */
  signature?: string;
  /** Optional partner URL for the "Manage your order" link; falls back to the Guest Checkout route. */
  manageYourOrderUrl?: string;
  featureName?: string;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  totalCostWithService?: number;
  taxInclusive?: boolean;
  taxBreakdown?: TaxBreakdownEntry[];
  totalNet?: number;
  totalGross?: number;
  taxLabel?: string;
  userId?: string | null;
  customerName?: string | null;
  bookingType?: string;
  /** Title of the offer the guest added to the booking, when they chose one. */
  selectedOffer?: string;
  depositAmount?: number;
  /**
   * The venue's deposit terms, repeated from the payment step so the guest can
   * still read what they agreed to. Venue-authored free text, rendered as plain
   * text. Omitted or blank hides the alert.
   */
  depositTerms?: string;
  /** Bookings created alongside this one when it is part of a linked booking. */
  additionalBookings?: LinkedBookingSummary[];
  /**
   * Shown straight under the header, above the cards, so the guest sees it
   * without scrolling (e.g. the partner `return_url` redirect notice).
   */
  notice?: React.ReactNode;
}

const PaymentConfirmationLayout: React.FC<PaymentConfirmationLayoutProps> = ({
  venueName,
  bookingRef,
  depositTotal,
  isAuthentication,
  preorderPaymentTaken,
  partySize,
  date,
  time,
  location,
  preOrderItems,
  bookingTypeGroups,
  currency,
  countryCode,
  mode = "full",
  shouldHidePrices,
  email,
  hideHeader = false,
  isPreorderCompletion = false,
  hidePricesByMenu,
  showBookingDetails = false,
  bookingJourney = false,
  isFromManageYourOrder,
  bookingId,
  signature,
  manageYourOrderUrl,
  featureName: featureNameProp,
  serviceChargeAmount,
  serviceChargePercentage,
  totalCostWithService,
  taxInclusive,
  taxBreakdown,
  totalNet,
  totalGross,
  taxLabel,
  userId: userIdProp,
  customerName: customerNameProp,
  bookingType,
  selectedOffer,
  depositAmount,
  depositTerms,
  additionalBookings,
  notice,
}) => {
  const isDetailedMode = mode === "full" || mode === "successWithDetails" || mode === "bookingWithPreorders";
  const showStandaloneReference = mode === "successOnly" && !showBookingDetails;
  const showPaymentSuccess = isDetailedMode || mode === "successOnly";
  const showBookingDetailsCard = showBookingDetails || isDetailedMode;
  const showWhatsNext = mode === "full";
  const trimmedDepositTerms = depositTerms?.trim();

  const buildManageYourOrderUrl = (): string | null => {
    const currentParams = new URLSearchParams(window.location.search);
    const resolvedSignature = signature ?? currentParams.get("signature");
    const resolvedBookingId = bookingId ?? currentParams.get("booking_id");
    if (!resolvedSignature || !resolvedBookingId) {
      return null;
    }

    if (manageYourOrderUrl) {
      try {
        const url = new URL(manageYourOrderUrl, window.location.origin);
        if (url.protocol !== "http:" && url.protocol !== "https:") {
          return null;
        }
        url.searchParams.set("booking_id", resolvedBookingId);
        url.searchParams.set("signature", resolvedSignature);
        return url.toString();
      } catch {
        return null;
      }
    }

    return `/manage-your-order?booking_id=${resolvedBookingId}&signature=${resolvedSignature}`;
  };

  return (
    <div id="gc-payment-confirmation" className="gcw:w-full gcw:px-bs-3">
      <div className="gcw:flex gcw:justify-center">
        <div className="gcw:w-full">
          {!hideHeader && (
            <BookingConfirmedHeader
              venueName={venueName}
              bookingRef={bookingRef}
              mode={mode}
              bookingJourney={bookingJourney}
            />
          )}
          {showStandaloneReference && (
            <BookingReference
              bookingRef={bookingRef}
              className="gcw:mb-bs-4 gcw:mt-bs-4 gcw:text-ref gcw:text-center"
            />
          )}
          {notice}
          <div className="gcw:flex gcw:flex-col gcw:w-full">
            <div className="">
              {showPaymentSuccess && (
                <PaymentSuccessCard
                  depositTotal={depositTotal}
                  currency={currency}
                  countryCode={countryCode}
                  isAuthentication={isAuthentication}
                  preorderPaymentTaken={preorderPaymentTaken}
                  mode={mode}
                  shouldHidePrices={shouldHidePrices}
                  email={email}
                  isPreorderCompletion={isPreorderCompletion}
                  bookingJourney={bookingJourney}
                />
              )}
              {/* The venue's deposit terms, repeated from the payment step */}
              {trimmedDepositTerms && (
                <AlertBanner
                  variant="info"
                  className="gcw:mb-4"
                  textClassName="gcw:whitespace-pre-line gcw:text-body gcw:text-text-secondary"
                >
                  {trimmedDepositTerms}
                </AlertBanner>
              )}
              {showBookingDetailsCard && (
                <BookingDetailsCard
                  partySize={partySize}
                  date={date}
                  time={time}
                  location={location}
                  preOrderItems={preOrderItems}
                  bookingTypeGroups={bookingTypeGroups}
                  currency={currency}
                  countryCode={countryCode}
                  shouldHidePrices={shouldHidePrices}
                  hidePricesByMenu={hidePricesByMenu}
                  serviceChargeAmount={serviceChargeAmount}
                  serviceChargePercentage={serviceChargePercentage}
                  totalCostWithService={totalCostWithService}
                  taxInclusive={taxInclusive}
                  taxBreakdown={taxBreakdown}
                  totalNet={totalNet}
                  totalGross={totalGross}
                  taxLabel={taxLabel}
                  customerName={customerNameProp ?? undefined}
                  bookingType={bookingType}
                  selectedOffer={selectedOffer}
                  bookingRef={bookingRef}
                  depositAmount={depositAmount}
                />
              )}
              {/* Each linked booking is a booking in its own right, so it gets its own details card */}
              <LinkedBookingsDetails
                bookings={additionalBookings ?? []}
                date={date}
                location={location}
                currency={currency}
                countryCode={countryCode}
                customerName={customerNameProp ?? undefined}
              />
              {showWhatsNext && (
                <div className="">
                  <WhatsNextCard />
                </div>
              )}
            </div>
            <FeatureSentiment
              featureName={featureNameProp}
              userId={userIdProp}
              customerName={customerNameProp}
              isBookingJourney={bookingJourney}
            />
            {isFromManageYourOrder && (
              <div id="gc-confirmation-actions">
                <PrimaryButton
                  text={t("manage_your_order", "Manage your order")}
                  className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button"
                  onClick={() => {
                    const manageUrl = buildManageYourOrderUrl();
                    if (manageUrl) {
                      window.open(manageUrl, "_parent", "noopener,noreferrer");
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmationLayout;
